import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";

const OCCASION_LABELS = {
  corporate: "Corporate / Office",
  birthday: "Birthday",
  wedding: "Wedding / Engagement",
  hosting: "Hosting at home",
  gifting: "Gifting",
  other: "Something else",
};

export default function BulkConfirm() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    api.get(`/bulk-orders/quote/${token}`)
      .then(({ data }) => { setDoc(data); if (data.status === "confirmed") setConfirmed(true); })
      .catch((e) => setError(e?.response?.data?.detail || "Quote not found."))
      .finally(() => setLoading(false));
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    try {
      await api.post(`/bulk-orders/quote/${token}/confirm`);
      setConfirmed(true);
      toast.success("Confirmed — we'll be in touch shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) return <div className="max-w-[900px] mx-auto px-6 py-32 text-espresso-light label-xs">Loading…</div>;
  if (error) return (
    <div className="max-w-[900px] mx-auto px-6 py-32">
      <div className="label-xs text-espresso-light">Quote unavailable</div>
      <h1 className="mt-4 font-serif text-4xl text-espresso">{error}</h1>
      <Link to="/bulk" className="btn-espresso mt-8 inline-block">Send a new request</Link>
    </div>
  );

  const q = doc.quote || {};
  const grouped = doc.selections || [];

  return (
    <main data-testid="bulk-confirm-page" className="max-w-[1000px] mx-auto px-6 md:px-10 py-12 md:py-20">
      <Link to="/" className="label-xs text-espresso-light link-underline">← Home</Link>

      <div className="mt-6 border-b border-line pb-8">
        <div className="label-xs text-gold">Your quote · {doc.reference}</div>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95] text-espresso">
          {confirmed ? (<>Confirmed.<br/><span className="italic text-espresso-light">We'll take it from here.</span></>) :
            (<>Ready when<br/><span className="italic text-espresso-light">you are.</span></>)}
        </h1>
      </div>

      <div className="mt-10 grid md:grid-cols-12 gap-10">
        <section className="md:col-span-7 space-y-8">
          <div>
            <div className="label-xs text-espresso-light">Occasion</div>
            <div className="mt-2 font-serif text-2xl text-espresso">
              {OCCASION_LABELS[doc.occasion] || doc.occasion}
              {doc.occasion_other && ` — ${doc.occasion_other}`}
            </div>
          </div>

          <div>
            <div className="label-xs text-espresso-light">Quantity · When</div>
            <div className="mt-2 font-serif text-2xl text-espresso">
              {doc.quantity} cups · {doc.date}{doc.time ? ` · ${doc.time}` : ""}
            </div>
            <div className="text-espresso-light capitalize mt-1">{doc.fulfillment}</div>
          </div>

          <div>
            <div className="label-xs text-espresso-light">Flavours{doc.curated ? " · curated" : ""}</div>
            <ul className="mt-3 space-y-1 text-espresso">
              {grouped.map((s, i) => (
                <li key={i}><span className="text-gold mr-1">·</span>{s.flavor} <span className="text-espresso-light">× {s.qty}</span></li>
              ))}
            </ul>
          </div>

          {q.notes && (
            <div className="border-l-2 border-gold pl-4 py-2">
              <div className="label-xs text-gold-hover">A note from us</div>
              <p className="mt-2 italic font-serif text-espresso leading-relaxed">{q.notes}</p>
            </div>
          )}
        </section>

        <aside className="md:col-span-5 border border-line p-8 h-fit md:sticky md:top-28" data-testid="quote-summary">
          <div className="label-xs text-espresso-light">Quote</div>
          <div className="mt-6 space-y-3 text-espresso">
            <div className="flex justify-between"><span>Price per cup</span><span>₹{Number(q.price_per_cup || 0).toFixed(0)}</span></div>
            <div className="flex justify-between"><span>Cups</span><span>× {doc.quantity}</span></div>
            <div className="flex justify-between"><span>Delivery / handling</span><span>₹{Number(q.delivery_fee || 0).toFixed(0)}</span></div>
          </div>
          <hr className="editorial-hr my-5" />
          <div className="flex justify-between items-baseline">
            <span className="label-xs">Total</span>
            <span className="font-serif text-3xl text-espresso" data-testid="quote-total">₹{Number(q.total || 0).toFixed(0)}</span>
          </div>
          {q.valid_until && <p className="mt-3 text-xs text-espresso-light">Valid until {q.valid_until}.</p>}

          {confirmed ? (
            <div className="mt-8 border border-gold/40 bg-gold/5 p-5 text-center" data-testid="bulk-confirmed">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gold text-ivory mb-3">
                <Check size={18} strokeWidth={2} />
              </div>
              <div className="font-serif text-xl text-espresso">Confirmed.</div>
              <p className="text-sm text-espresso-light mt-2">
                Our team will call you on <span className="text-espresso">{doc.customer?.mobile}</span> shortly with the next steps.
              </p>
            </div>
          ) : (
            <>
              <button
                onClick={confirm}
                disabled={confirming}
                data-testid="bulk-confirm-btn"
                className="btn-espresso w-full mt-8 disabled:opacity-40"
              >
                {confirming ? "Confirming…" : "Confirm this quote"}
              </button>
              <p className="text-xs text-espresso-light mt-4">
                Have a question? Reply to the email — we personally read every note.
              </p>
            </>
          )}
        </aside>
      </div>
    </main>
  );
}
